import type { RowDiffKind } from './rowDiff';
import type { EditableRowVariant } from '../types';

/** Border + tiny label chip inside a cell shell (New / Edited / Deleted). */
export interface CellStatusVisual {
  label: string;
  border: string;
  chip: string;
}

export function computeCellStatusVisual(
  fieldId: string,
  variant: EditableRowVariant,
  rowDiff: RowDiffKind,
  modifiedFieldIds: Set<string>
): CellStatusVisual | null {
  if (variant === 'draft') {
    return null;
  }
  if (variant === 'deleted') {
    return {
      label: 'Deleted',
      border: 'border-muted-foreground/45 bg-muted/25',
      chip: 'border-muted-foreground/30 bg-muted/90 text-muted-foreground',
    };
  }
  if (rowDiff === 'new') {
    return {
      label: 'New',
      border: 'border-emerald-500/55 bg-background',
      chip: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200',
    };
  }
  if (rowDiff === 'modified' && modifiedFieldIds.has(fieldId)) {
    return {
      label: 'Edited',
      border: 'border-amber-500/65 bg-background',
      chip: 'border-amber-500/35 bg-amber-500/10 text-amber-950 dark:text-amber-100',
    };
  }
  return null;
}

/** Invalid cell: red border + error chip (takes precedence over diff / New / Edited styling). */
export function computeValidationCellVisual(message: string): CellStatusVisual {
  return {
    label: message,
    border: 'border-destructive bg-destructive/5',
    chip: 'border-destructive/55 bg-destructive/15 text-destructive dark:text-destructive',
  };
}

export function rowVariantWrapperClass(
  variant: EditableRowVariant,
  rowDiff: RowDiffKind | undefined
): string {
  if (variant === 'deleted') {
    return 'rounded-md border border-dashed border-muted-foreground/35 bg-muted/40 px-1 py-0.5 opacity-90';
  }
  if (variant === 'draft') {
    return '';
  }
  if (rowDiff === 'new') {
    return 'rounded-md ring-1 ring-inset ring-emerald-500/45 bg-emerald-500/15 px-1.5 py-1.5 m-0.5 dark:ring-emerald-400/40 dark:bg-emerald-500/18';
  }
  if (rowDiff === 'modified') {
    return 'rounded-md px-1 py-0.5';
  }
  return 'px-1 py-0.5';
}
